import { useMemo, useState, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { Menu, Text, TouchableRipple } from 'react-native-paper';
import Animated, { useAnimatedStyle } from 'react-native-reanimated';

import { CheckIcon, DownIcon } from '@/assets';
import { useMeinOrtScroll } from '@/features/mein-ort/components/MeinOrtScrollContext';
import { getNewsFilterOptions } from '@/features/mein-ort/components/news-filter/newsFilterOptions';
import type { MeinOrtNewsFilter } from '@/features/mein-ort/components/news-filter/newsFilterTypes';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { ExtendedMenu } from '@/shared/components/ExtendedMenu';
import { Row } from '@/shared/components/layout/Row';
import { useAppTheme } from '@/styles/hooks/useAppTheme';
import type { AppTheme } from '@/styles/theme';

const ROW_HEIGHT = 48;
const ROW_MARGIN_TOP = 4;

export const NEWS_FILTER_HEIGHT = ROW_HEIGHT + ROW_MARGIN_TOP;

const makeStyles = (theme: AppTheme) =>
  StyleSheet.create({
    container: {
      position: 'absolute',
      left: 0,
      right: 0,
      zIndex: 1
    },
    bar: {
      height: ROW_HEIGHT,
      marginTop: ROW_MARGIN_TOP,
      alignItems: 'center',
      justifyContent: 'flex-end',
      paddingHorizontal: 12
    },
    trigger: { borderRadius: 32 },
    triggerHover: { backgroundColor: theme.stateLayers.hover },
    triggerContent: {
      alignItems: 'center',
      paddingLeft: 12
    },
    triggerLabel: {
      fontSize: 16,
      lineHeight: 24,
      fontWeight: '500',
      letterSpacing: 0.15,
      color: theme.colors.primary
    },
    chevron: { padding: 8 }
  });

type MeinOrtNewsFilterRowProps = {
  value: MeinOrtNewsFilter;
  onSelect: (value: MeinOrtNewsFilter) => void;
};

export const MeinOrtNewsFilterRow: FC<MeinOrtNewsFilterRowProps> = ({
  value,
  onSelect
}) => {
  const theme = useAppTheme();
  const styles = useMemo(() => makeStyles(theme), [theme]);
  const { t } = useTranslation(LocalizationNameSpace.MY_PLACE, {
    keyPrefix: 'screens.myPlace.newsFilter'
  });
  const { scrollY, feedInset } = useMeinOrtScroll();
  const [open, setOpen] = useState(false);
  const [buttonHover, setButtonHover] = useState(false);
  const options = getNewsFilterOptions(t);
  const activeOption = options.find((o) => o.value === value) ?? options[0];

  const containerStyle = useAnimatedStyle(() => {
    'worklet';
    return { transform: [{ translateY: -Math.max(scrollY.value, 0) }] };
  });

  return (
    <Animated.View
      style={[styles.container, { top: feedInset }, containerStyle]}
    >
      <Row style={styles.bar}>
        <ExtendedMenu
          visible={open}
          onDismiss={() => setOpen(false)}
          backgroundColor={theme.colors.surface}
          anchor={
            <TouchableRipple
              testID="mein-ort-news-filter-trigger"
              onPress={() => setOpen(true)}
              onHoverIn={() => setButtonHover(true)}
              onHoverOut={() => setButtonHover(false)}
              rippleColor={theme.rippleColors.onSurface}
              borderless
              style={[styles.trigger, buttonHover && styles.triggerHover]}
            >
              <Row style={styles.triggerContent}>
                <Text style={styles.triggerLabel}>{activeOption?.label}</Text>
                <View style={styles.chevron}>
                  <DownIcon
                    fill={theme.colors.primary}
                    width={24}
                    height={24}
                  />
                </View>
              </Row>
            </TouchableRipple>
          }
        >
          {options.map((o) => {
            const active = o.value === value;
            return (
              <Menu.Item
                key={String(o.value)}
                testID={`mein-ort-news-filter-option-${String(o.value)}`}
                title={o.label}
                onPress={() => {
                  onSelect(o.value);
                  setOpen(false);
                }}
                trailingIcon={({ color, size }) =>
                  active ? (
                    <CheckIcon fill={color} width={size} height={size} />
                  ) : undefined
                }
              />
            );
          })}
        </ExtendedMenu>
      </Row>
    </Animated.View>
  );
};
